import { useState } from "react";
import { requisitionAcknowledgeFlag } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { Empty, ReasonDialog, humanizeDates, ISO_IN_TEXT, fmtDate } from "@/ui";
import type { TodayRow } from "@/views/today/families";

/** A row whose state is settled: it reports, it does not ask. §4.1's read-only rows carry a link, not a button. */
const isAwareness = (row: TodayRow) => ["waiting", "upcoming", "notice", "receipt"].includes(row.kind);

export function Section({ title, count, rows, empty }: { title: string; count: number; rows: TodayRow[]; empty: string }) {
  return (
    <section>
      <h2 className="sec">
        {title} <span className="sec__n">{count}</span>
      </h2>
      {rows.length === 0 ? <Empty heading={`${title} is clear`}>{empty}</Empty> : <ul className="rows">{rows.map((r) => <RowItem key={r.id} row={r} />)}</ul>}
    </section>
  );
}

export function RowItem({ row }: { row: TodayRow }) {
  const [ack, setAck] = useState(false);
  const acknowledge = useWrite((i: { requisition_id: string; note: string }, k) => requisitionAcknowledgeFlag(i, k), [["today"], ["requisitions"]]);
  const isFlag = row.kind === "flag" && row.ref_type === "requisition" && !!row.ref_id;
  const forPerson = row.for_person_name; // §4.20: a delegated row says whose it is
  // B2 S10: a hold, cancel or reopen request (and a renege's reopening) opens its HRBP approval card on the requisition
  const awareness = isAwareness(row);
  return (
    <li className={"rowitem" + (row.overdue ? " rowitem--overdue" : "")}>
      <span className="rowitem__glyph" aria-hidden="true">
        {row.overdue ? "▲" : row.kind === "notice" || row.glyph === "." ? "◦" : row.glyph}
      </span>
      <span className="sr-only">{row.overdue ? "Overdue" : row.kind === "notice" ? "Notice" : awareness ? "Upcoming or waiting" : "Attention required"}</span>
      <div className="rowitem__body">
        <div className="rowitem__title">{row.title}</div>
        {/* EM-25: the date this is owed on is read without opening the record — its own line, never the muted meta ink. */}
        {row.due_at && (
          <div className={"rowitem__due" + (row.overdue ? " rowitem__due--overdue" : "")}>
            {row.overdue ? "Overdue — was due " : "Due "}
            {fmtDate(row.due_at, true)}
          </div>
        )}
        <div className="rowitem__meta muted">
          {row.body ? humanizeDates(row.body) : ""}
          {row.due_at || row.body?.match(ISO_IN_TEXT) ? "" : `${row.body ? " · " : ""}${fmtDate(row.created_at, true)}`}
          {forPerson ? ` · for ${forPerson}` : ""}
        </div>
      </div>
      {isFlag ? (
        <>
          <button className="btn btn--sm btn--primary" onClick={() => setAck(true)}>
            Acknowledge
          </button>
          <ReasonDialog title="Acknowledge the phase flag" label="Note to the Recruiter" confirm="Acknowledge" open={ack} onClose={() => setAck(false)} busy={acknowledge.isPending} error={acknowledge.error} onConfirm={(note) => void acknowledge.mutateAsync({ requisition_id: row.ref_id!, note }).then(() => setAck(false))}>
            <p className="muted">{row.body}</p>
          </ReasonDialog>
        </>
      ) : (
        row.route && (
          <a className={awareness ? "rowitem__open" : "btn btn--sm"} href={row.route} aria-label={`Open ${row.title}`}>
            Open <span aria-hidden="true">→</span>
          </a>
        )
      )}
    </li>
  );
}
